import { useCallback, useEffect, useState } from 'react';
import type { AdminUser, RecentAccount } from '@api/types';

const STORAGE_KEY = 'admin_recent_accounts';
const MAX_ENTRIES = 5;

/**
 * Lista persistente (localStorage) de cuentas que se han logueado desde este
 * navegador. Alimenta el saludo personalizado del login y la sección
 * "OTRAS CUENTAS". NO contiene tokens — solo nombre, email, rol y timestamp.
 *
 * Decisiones:
 * - Logout no limpia la lista (la cuenta sigue visible como "última vista").
 * - Solo el botón × en cada card la elimina explícitamente.
 * - Cota dura: MAX_ENTRIES = 5; la más antigua se descarta al insertar.
 * - Cross-tab: se escucha el storage event para sincronizar el listado.
 */
export interface UseRecentAccountsReturn {
  accounts: readonly RecentAccount[];
  /** Inserta o actualiza la entrada para `user.email`, marcando lastLoginAt = now. */
  upsert: (user: AdminUser) => void;
  /** Quita la entrada cuyo email coincide. */
  remove: (email: string) => void;
  /** Limpia toda la lista (no se usa habitualmente — útil para tests). */
  clear: () => void;
}

function read(): RecentAccount[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is RecentAccount =>
        typeof e === 'object' &&
        e !== null &&
        typeof (e as RecentAccount).email === 'string' &&
        typeof (e as RecentAccount).lastLoginAt === 'string',
    );
  } catch {
    return [];
  }
}

function write(list: readonly RecentAccount[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* localStorage puede estar lleno o deshabilitado; degrada silencioso */
  }
}

function sortAndCap(list: readonly RecentAccount[]): RecentAccount[] {
  return [...list]
    .sort((a, b) => b.lastLoginAt.localeCompare(a.lastLoginAt))
    .slice(0, MAX_ENTRIES);
}

export function useRecentAccounts(): UseRecentAccountsReturn {
  const [accounts, setAccounts] = useState<readonly RecentAccount[]>(() => sortAndCap(read()));

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY) setAccounts(sortAndCap(read()));
    }
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener('storage', onStorage); };
  }, []);

  const upsert = useCallback((user: AdminUser) => {
    setAccounts((prev) => {
      const next = sortAndCap([
        {
          id: user.id,
          email: user.email,
          name: user.name,
          role: user.role,
          lastLoginAt: new Date().toISOString(),
        },
        ...prev.filter((a) => a.email !== user.email),
      ]);
      write(next);
      return next;
    });
  }, []);

  const remove = useCallback((email: string) => {
    setAccounts((prev) => {
      const next = prev.filter((a) => a.email !== email);
      write(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    write([]);
    setAccounts([]);
  }, []);

  return { accounts, upsert, remove, clear };
}
